import { ArrowUpRight, ArrowDownRight } from "lucide-react";

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6" data-testid={testid}>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1E2025]">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      {Icon && (
        <div className="h-14 w-14 rounded-2xl bg-[#00C9E0]/10 flex items-center justify-center mb-4 ring-1 ring-[#00C9E0]/15">
          <Icon className="h-6 w-6 text-[#00C9E0]" />
        </div>
      )}
      <h3 className="font-display font-semibold text-slate-800">{title}</h3>
      {hint && <p className="text-sm text-slate-500 mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-md bg-slate-200/70 ${className}`} />;
}

const TONES = {
  cyan: { icon: "text-[#0094A8] bg-[#00C9E0]/10", ring: "group-hover:border-[#00C9E0]/50" },
  green: { icon: "text-[#5B880C] bg-[#9ACC32]/15", ring: "group-hover:border-[#9ACC32]/50" },
  pink: { icon: "text-[#D01A46] bg-[#FE3967]/15", ring: "group-hover:border-[#FE3967]/50" },
  orange: { icon: "text-[#C42607] bg-[#FE421C]/15", ring: "group-hover:border-[#FE421C]/50" },
  navy: { icon: "text-[#0F1724] bg-[#0F1724]/8", ring: "group-hover:border-slate-400" },
};

export function StatCard({ label, value, icon: Icon, tone = "cyan", sub, trend, onClick }) {
  const t = TONES[tone] || TONES.cyan;
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      data-testid={`kpi-${String(label).toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
      className={`cc-card group p-5 text-left w-full transition-all duration-200 ${t.ring} hover:shadow-[0_10px_15px_-3px_rgba(15,23,42,0.08)] ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <p className="cc-overline">{label}</p>
        {Icon && (
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${t.icon}`}>
            <Icon className="h-[18px] w-[18px]" />
          </div>
        )}
      </div>
      <p className="mt-3 font-display text-[1.7rem] leading-none font-bold text-[#1E2025] tnum">{value}</p>
      <div className="mt-2 flex items-center gap-2">
        {trend && (
          <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${trend.dir === "down" ? "bg-[#FE421C]/10 text-[#C42607]" : "bg-[#9ACC32]/15 text-[#5B880C]"}`}>
            {trend.dir === "down" ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
            {trend.value}
          </span>
        )}
        {sub && <p className="text-xs text-slate-400">{sub}</p>}
      </div>
    </Comp>
  );
}

export function SectionCard({ title, action, children, className = "" }) {
  return (
    <div className={`cc-card p-5 sm:p-6 ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h2 className="font-display text-base font-semibold text-[#1E2025]">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}
